import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { Textarea } from "../ui/textarea";
import {
  appendNoDuplicateRecordNumberRefine,
  multipleprintSchema,
} from "@/api/multipleprint/multipleprint-validation";
import { useCreateMultipleprint } from "@/api/multipleprint/multipleprint";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import { getMainLanguage, SupportedLocale } from "@/i18n";
import { SelectField } from "@/components/form-items/AbstractForm";
import {
  multipleprintNatureFieldLabel,
  multipleprintNatureOptions,
} from "@/lib/multipleprint-nature";
import { MULTIPLEPRINT_NATURES, MultipleprintNature } from "@/lib/types";

type FormType = {
  estampesMultiplesDescriptionNumeroEnregistrement: string;
  estampesMultiplesDescriptionTitre: string;
  typeOeuvre: string;
  estampesMultiplesNature: MultipleprintNature;
};

type NewMultiprintDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewMultiprintDialog = ({
  onOpenChange,
  open,
}: NewMultiprintDialogProps) => {
  const { t } = useTranslation();
  const mainLanguage = getMainLanguage() as SupportedLocale;
  const schema = useMemo(
    () =>
      appendNoDuplicateRecordNumberRefine(
        multipleprintSchema
          .pick({
            estampesMultiplesDescriptionNumeroEnregistrement: true,
            estampesMultiplesDescriptionTitre: true,
          })
          .extend({
            typeOeuvre: z.string(),
            estampesMultiplesNature: z.enum(MULTIPLEPRINT_NATURES),
          }),
      ),
    [],
  );
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    defaultValues: {
      estampesMultiplesDescriptionNumeroEnregistrement: "",
      estampesMultiplesDescriptionTitre: "",
      typeOeuvre: "",
      estampesMultiplesNature: "simple",
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });
  const { mutateAsync } = useCreateMultipleprint();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      form.reset({
        estampesMultiplesDescriptionNumeroEnregistrement: "",
        estampesMultiplesDescriptionTitre: "",
        typeOeuvre: "",
        estampesMultiplesNature: "simple",
      });
    }
  }, [open, form]);

  const onFormSubmit = async (data: FormType) => {
    try {
      const result = await mutateAsync({
        estampesMultiplesDescriptionNumeroEnregistrement:
          data.estampesMultiplesDescriptionNumeroEnregistrement,
        estampesMultiplesDescriptionTitre: data.estampesMultiplesDescriptionTitre,
        estampesMultiplesNature: data.estampesMultiplesNature,
        ...(data.typeOeuvre.trim()
          ? {
              estampesMultiplesDescriptionTypeMultilingual: {
                [mainLanguage]: data.typeOeuvre.trim(),
              },
            }
          : {}),
      });
      navigate(`/edit/multipleprint/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("estampesMultiplesDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createMmultipleprint")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="estampesMultiplesDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>
              {t("estampesMultiplesDescriptionNumeroEnregistrement")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <SelectField
        name="estampesMultiplesNature"
        label={multipleprintNatureFieldLabel(t)}
        options={multipleprintNatureOptions(t)}
        nonMaskable
      />
      <ModifiableList
        name="typeOeuvre"
        label={t("estampesMultiplesDescriptionType")}
        listKey="liste-types-oeuvres"
        lang={mainLanguage}
      />
      <FormField
        control={form.control}
        name="estampesMultiplesDescriptionTitre"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("estampesMultiplesDescriptionTitre")}</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewMultiprintDialog;
